/**
 * Create form for a field event.
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { Button } from '@/components/forms/Button';
import { EventActivitySection } from './event-form-builder/EventActivitySection';
import { EventFormatSection } from './event-form-builder/EventFormatSection';
import { EventGeneralSection } from './event-form-builder/EventGeneralSection';
import { EventParticipantsSection } from './event-form-builder/EventParticipantsSection';
import { EventScheduleSection } from './event-form-builder/EventScheduleSection';
import { eventsService } from '@/services/events.service';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import { eventValidationSchema } from '@/utils/validation-schemas-utils';
import {
  applyEventFieldSideEffects,
  buildCreateEventDto,
  DEFAULT_CREATE_EVENT_DATA,
} from '@/components/events/event-form-utils';
import type { CreateEventDto, UnregisteredStudentData } from '@/types/event.types';
import type { UserWithProfile } from '@/types/user.types';

/** Props for {@link CreateEventView}. */
interface CreateEventViewProps {
  onClose: () => void;
  onSuccess: () => void;
  onRequestCreateTutor?: () => void;
}

/** Create form for a field event. */
export const CreateEventView: React.FC<CreateEventViewProps> = ({
  onClose,
  onSuccess,
  onRequestCreateTutor,
}) => {
  const [eventData, setEventData] =
    React.useState<Partial<CreateEventDto>>(DEFAULT_CREATE_EVENT_DATA);

  const [selectedOrganizers, setSelectedOrganizers] = React.useState<UserWithProfile[]>([]);
  const [selectedStudents, setSelectedStudents] = React.useState<UserWithProfile[]>([]);
  const [unregisteredParticipants, setUnregisteredParticipants] = React.useState<
    UnregisteredStudentData[]
  >([]);

  const { establishments } = useSessionFormData();

  const establishmentName = React.useMemo(
    () => establishments.find((e) => e.id === eventData.establishmentId)?.name,
    [establishments, eventData.establishmentId],
  );

  const validation = useFormValidation(eventValidationSchema);

  const createMutation = useMutation({
    mutationFn: (data: CreateEventDto) => eventsService.createEvent(data),
    onSuccess: () => {
      toast.success('Événement créé avec succès');
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Validate (mark all fields touched on failure), lock format, attach participant ids, then POST. */
  function handleFieldChange(field: string, value: unknown): void {
    const updatedData = applyEventFieldSideEffects(eventData, field, value);
    setEventData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    if (field === 'startTime') validation.markAsTouched('endTime');
    if (field === 'eventDate' && eventData.eventEndDate) validation.markAsTouched('eventEndDate');
  }

  function handleBlur(field: string): void {
    validation.markAsTouched(field);
  }

  const handleSubmit = () => {
    if (!validation.validateForm(eventData)) {
      Object.keys(eventValidationSchema).forEach((field) => validation.markAsTouched(field));
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    createMutation.mutate(
      buildCreateEventDto(
        eventData,
        selectedOrganizers,
        selectedStudents,
        unregisteredParticipants,
      ),
    );
  };

  const selectedOrganizerIds = React.useMemo(
    () => selectedOrganizers.map((o) => o.id),
    [selectedOrganizers],
  );
  const selectedStudentIds = React.useMemo(
    () => selectedStudents.map((s) => s.id),
    [selectedStudents],
  );

  return (
    <div className="collapse-replace-form">
      <div className="collapse-replace-form-header">
        <div className="collapse-replace-form-header-content">
          <div
            className="collapse-replace-form-icon border-2"
            style={{
              background: 'color-mix(in srgb, var(--color-2-nuance-1) 10%, transparent)',
              borderColor: 'var(--color-2-nuance-1)',
            }}
          >
            <Plus className="w-5 h-5" style={{ color: 'var(--color-2-nuance-1)' }} />
          </div>
          <div>
            <h2 className="collapse-replace-form-title">Créer un événement</h2>
            <p className="collapse-replace-form-subtitle">
              Renseignez les informations de l'événement
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          icon={X}
          onClick={onClose}
          disabled={createMutation.isPending}
        />
      </div>

      <div className="collapse-replace-form-content space-y-6">
        <EventGeneralSection
          form={eventData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
        />
        <EventScheduleSection
          form={eventData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
        />
        <EventActivitySection
          form={eventData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
        />
        <EventFormatSection form={eventData} onFieldChange={handleFieldChange} />

        <div className="border-t border-[var(--color-1-nuance-3)] pt-6">
          <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--color-1-nuance-1)' }}>
            Participants
          </h3>
          <EventParticipantsSection
            selectedOrganizers={selectedOrganizers}
            selectedStudents={selectedStudents}
            unregisteredParticipants={unregisteredParticipants}
            selectedOrganizerIds={selectedOrganizerIds}
            selectedStudentIds={selectedStudentIds}
            onAddOrganizer={(user) => setSelectedOrganizers((prev) => [...prev, user])}
            onRemoveOrganizer={(id) =>
              setSelectedOrganizers((prev) => prev.filter((o) => o.id !== id))
            }
            onAddStudent={(user) => setSelectedStudents((prev) => [...prev, user])}
            onRemoveStudent={(id) => setSelectedStudents((prev) => prev.filter((s) => s.id !== id))}
            onAddUnregistered={(data) => setUnregisteredParticipants((prev) => [...prev, data])}
            onRemoveUnregistered={(idx) =>
              setUnregisteredParticipants((prev) => prev.filter((_, i) => i !== idx))
            }
            onEditUnregistered={(idx, data) =>
              setUnregisteredParticipants((prev) => prev.map((p, i) => (i === idx ? data : p)))
            }
            establishmentId={eventData.establishmentId}
            establishmentName={establishmentName}
            onRequestCreateTutor={onRequestCreateTutor}
          />
        </div>
      </div>

      <div className="collapse-replace-form-footer justify-end">
        <Button preset="form-cancel" onClick={onClose} disabled={createMutation.isPending}>
          Annuler
        </Button>
        <Button preset="form-save" onClick={handleSubmit} loading={createMutation.isPending}>
          Créer
        </Button>
      </div>
    </div>
  );
};
